import React, { useState, useEffect } from 'react';
import { Modal, Input, Button, Alert } from 'antd';

/** Mínimo de caracteres da justificativa (espelha a validação da API). */
export const JUSTIFICATION_MIN = 5;

/**
 * Campo de justificativa reutilizável (textarea com contador e aria-label).
 * Usado dentro dos modais de edição dos painéis e no JustificationModal.
 * Quando `required` é true (comprador/líder), a API rejeita sem justificativa.
 *
 * @param {string} value Texto atual.
 * @param {Function} onChange Recebe o novo texto.
 * @param {boolean} required Se a justificativa é obrigatória (não-admin).
 */
export const JustificationInput = ({ value, onChange, required = true }) => (
  <div>
    <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 6 }}>
      Justificativa {required ? <span style={{ color: '#dc2626' }}>*</span> : '(opcional)'}
    </div>
    <Input.TextArea
      value={value}
      onChange={(e) => onChange(e.target.value)}
      autoSize={{ minRows: 2, maxRows: 4 }}
      maxLength={500}
      showCount
      placeholder={`Explique o motivo da correção (mínimo ${JUSTIFICATION_MIN} caracteres). Aparece na auditoria.`}
      aria-label="Justificativa da correção"
      aria-required={required}
    />
  </div>
);

/**
 * Retorna true se a justificativa satisfaz a regra (obrigatória com mínimo de
 * caracteres para não-admin; sempre ok para admin).
 */
export const isJustificationValid = (value, required = true) => {
  if (!required) return true;
  return String(value || '').trim().length >= JUSTIFICATION_MIN;
};

/**
 * Modal de confirmação com justificativa obrigatória.
 *
 * Usado nas exclusões (e confirmações destrutivas) feitas por
 * Operador-comprador/Líder no PWA: a API exige a justificativa, que é gravada
 * no AuditLog. Para Admin a justificativa é opcional (required=false).
 *
 * Props:
 * - open: controla a visibilidade.
 * - title: título do modal.
 * - description: texto/nó explicando o que será feito (ex.: resumo do registro).
 * - confirmLabel: rótulo do botão de confirmação (padrão "Confirmar").
 * - danger: pinta o botão de confirmação como destrutivo.
 * - loading: estado de carregamento do botão.
 * - required: se a justificativa é obrigatória (padrão true).
 * - onConfirm(justification): chamado com a justificativa digitada (trim).
 * - onCancel: fecha o modal.
 */
const JustificationModal = ({
  open,
  title = 'Confirmar correção',
  description = null,
  confirmLabel = 'Confirmar',
  danger = false,
  loading = false,
  required = true,
  onConfirm,
  onCancel,
}) => {
  const [justification, setJustification] = useState('');

  // Limpa o campo sempre que o modal reabre (evita justificativa "vazada" de
  // uma correção anterior).
  useEffect(() => {
    if (open) setJustification('');
  }, [open]);

  const valid = isJustificationValid(justification, required);

  return (
    <Modal
      title={title}
      open={open}
      onCancel={onCancel}
      centered
      footer={[
        <Button key="cancel" onClick={onCancel}>Cancelar</Button>,
        <Button
          key="ok"
          type="primary"
          danger={danger}
          loading={loading}
          disabled={!valid}
          onClick={() => onConfirm(String(justification).trim())}
        >
          {confirmLabel}
        </Button>,
      ]}
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        {description && <div>{description}</div>}
        {required && (
          <Alert
            type="info"
            showIcon
            message="A justificativa é obrigatória e ficará registrada nos logs de auditoria."
          />
        )}
        <JustificationInput value={justification} onChange={setJustification} required={required} />
      </div>
    </Modal>
  );
};

export default JustificationModal;
